import { LoadingState } from '../ui';
// src/pages/UserProfile.jsx

import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useNavigate } from "react-router-dom";
import { isAtLeastAge } from "../lib/ageUtils";
import SidebarNavbar from "../components/SidebarNavbar";
import BottomNavbar from "../components/BottomNavbar";
import ShareButton from "../components/ShareButton";
import notify from '../lib/notify';
import { trackReferralCopy } from '../hooks/useAnalytics';
import { QRCodeSVG } from 'qrcode.react';
import SubscriptionCard from "../components/SubscriptionCard";
import { Button as FcButton, Card as FcCard, Input as FcInput, Select as FcSelect } from '../ui';
import { Tabs as FcTabs } from '../ui';

export default function UserProfile({ session, userRole, username }) {
  const navigate = useNavigate();
  
  // Logout handler
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('account');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state for editing
  const [formData, setFormData] = useState({
    username: '',
    date_of_birth: '',
    gender: '',
    time_zone: ''
  });
  
  // Referral state
  const [referralData, setReferralData] = useState({
    referralCode: '',
    totalReferrals: 0,
    availableTickets: 0,
    recentReferrals: []
  });
  
  // Stats state
  const [stats, setStats] = useState({
    totalCompetitions: 0,
    activeCompetitions: 0,
    totalWins: 0,
    memberSince: null
  });

  useEffect(() => {
    if (session?.user?.id) {
      fetchProfile();
      fetchReferralData(session.user.id);
      fetchStats(session.user.id);
    }
  }, [session?.user?.id]);

  const fetchProfile = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate("/login");
      return;
    }

    const { data, error } = await supabase
      .from("profiles")
      .select("username, date_of_birth, gender, time_zone, notifications_enabled, created_at")
      .eq("id", user.id)
      .single();

    if (error) {
    } else {
      setProfile(data);
      setFormData({
        username: data.username || '',
        date_of_birth: data.date_of_birth || '',
        gender: data.gender || '',
        time_zone: data.time_zone || ''
      });
      setStats(prev => ({
        ...prev,
        memberSince: data.created_at
      }));
    }

    setLoading(false);
  };

  const fetchReferralData = async (userId) => {
    try {
      // Fetch referral code
      const { data: codeData, error: codeError } = await supabase
        .from('user_referral_codes')
        .select('referral_code')
        .eq('user_id', userId)
        .single();

      if (codeError) {
      }

      // Fetch total successful referrals
      const { count: referralCount, error: countError } = await supabase
        .from('referral_signups')
        .select('*', { count: 'exact', head: true })
        .eq('referrer_user_id', userId)
        .eq('signup_completed', true);

      if (countError) {
      }

      // Fetch available referral tickets - set to 0 if error
      let ticketCount = 0;
      try {
        const { count, error: ticketError } = await supabase
          .from('tickets')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('ticket_type', 'referral')
          .eq('used', false);

        if (ticketError) {
          ticketCount = 0;
        } else {
          ticketCount = count || 0;
        }
      } catch (err) {
        ticketCount = 0;
      }

      // Fetch recent referrals (two-step query to avoid foreign key relationship issues)
      const { data: recentData, error: recentError } = await supabase
        .from('referral_signups')
        .select('referred_user_id, completed_at')
        .eq('referrer_user_id', userId)
        .eq('signup_completed', true)
        .order('completed_at', { ascending: false })
        .limit(10);

      if (recentError) {
      }

      // Fetch usernames for the referred users
      let recentReferralsWithUsernames = [];
      if (recentData && recentData.length > 0) {
        const userIds = recentData.map(r => r.referred_user_id).filter(Boolean);
        
        if (userIds.length > 0) {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('id, username')
            .in('id', userIds);

          if (profileError) {
          }

          // Map usernames back to referral data
          recentReferralsWithUsernames = recentData.map(referral => ({
            ...referral,
            profiles: profileData?.find(p => p.id === referral.referred_user_id)
          }));
        }
      }

      setReferralData({
        referralCode: codeData?.referral_code || '',
        totalReferrals: referralCount || 0,
        availableTickets: ticketCount || 0,
        recentReferrals: recentReferralsWithUsernames || []
      });
    } catch (error) {
    }
  };

  const fetchStats = async (userId) => {
    try {
      // Use the same trophy_room_stats_view that Trophy Room uses for consistency
      const { data: competitionStats, error: compError } = await supabase
        .from('trophy_room_stats_view')
        .select('competition_id, competition_type, competition_status, user_status, is_winner')
        .eq('user_id', userId);

      if (compError) {
      }

      // Get scout entries separately (not in trophy_room_stats_view)
      const { data: scoutEntries, error: scoutError } = await supabase
        .from('scout_entries')
        .select('competition_id')
        .eq('user_id', userId);

      if (scoutError) {
      }

      const allStats = competitionStats || [];
      const scouts = scoutEntries || [];

      // Filter out scout competitions from main stats
      const mainCompetitions = allStats.filter(comp => comp.competition_type !== 'scout');
      
      // Total competitions (main + scout, deduplicated)
      const mainCompIds = new Set(mainCompetitions.map(c => c.competition_id));
      const scoutCompIds = new Set(scouts.map(s => s.competition_id));
      const allCompIds = new Set([...mainCompIds, ...scoutCompIds]);
      const totalCount = allCompIds.size;

      // Active competitions: where user is NOT eliminated AND competition status is 'active'
      // user_status can be 'eliminated', 'active', 'Still In', etc.
      // Competition status is 'active' when running, 'inactive' when ended
      const activeCount = mainCompetitions.filter(
        comp => comp.competition_status === 'active' && comp.user_status !== 'eliminated'
      ).length + scouts.length; // Scout entries are always "active" until competition ends

      // Wins: use the is_winner flag from the view (handles both knockout and season logic)
      const winsCount = allStats.filter(comp => comp.is_winner === true).length;

      setStats(prev => ({
        ...prev,
        totalCompetitions: totalCount,
        activeCompetitions: activeCount,
        totalWins: winsCount
      }));
    } catch (error) {
    }
  };

  const handleToggleNotifications = async () => {
    const newValue = !profile.notifications_enabled;
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const { error } = await supabase
      .from("profiles")
      .update({ notifications_enabled: newValue })
      .eq("id", user.id);

    if (!error) {
      setProfile({ ...profile, notifications_enabled: newValue });
      notify(`Notifications ${newValue ? 'enabled' : 'disabled'}`, 'success');
    } else {
      notify('Failed to update notification setting', 'error');
    }
  };

  const handleSaveProfile = async () => {
    if (!isAtLeastAge(formData.date_of_birth, 13)) {
      notify('You must be at least 13 years old to use Footy Coach.', 'error');
      return;
    }

    setSaving(true);
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const { error } = await supabase
      .from("profiles")
      .update({
        username: formData.username,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        time_zone: formData.time_zone
      })
      .eq("id", user.id);

    if (!error) {
      setProfile({ ...profile, ...formData });
      setEditing(false);
      notify('Profile updated successfully!', 'success');
    } else {
      notify('Failed to update profile', 'error');
    }
    setSaving(false);
  };

  const getReferralBadge = (count) => {
    if (count >= 50) return { name: 'Gold Referrer', icon: '🥇', color: 'from-yellow-400 to-yellow-600', next: null };
    if (count >= 25) return { name: 'Silver Referrer', icon: '🥈', color: 'from-gray-300 to-gray-500', next: { count: 50, name: 'Gold' } };
    if (count >= 5) return { name: 'Bronze Referrer', icon: '🥉', color: 'from-orange-400 to-orange-600', next: { count: 25, name: 'Silver' } };
    return { name: 'No Badge Yet', icon: '🎯', color: 'from-gray-200 to-gray-400', next: { count: 5, name: 'Bronze' } };
  };

  const badge = getReferralBadge(referralData.totalReferrals);
  const progressToNext = badge.next 
    ? ((referralData.totalReferrals / badge.next.count) * 100)
    : 100;

  const getInitials = () => {
    if (!profile?.username) return '?';
    return profile.username.substring(0, 2).toUpperCase();
  };

  const formatMemberSince = () => {
    if (!stats.memberSince) return 'Recently';
    const date = new Date(stats.memberSince);
    return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  };



  const renderTabContent = () => {
    switch (activeTab) {
      case 'account':
        return renderAccountTab();
      case 'referrals':
        return renderReferralsTab();
      case 'preferences':
        return renderPreferencesTab();
      case 'notifications':
        return renderNotificationsTab();
      default:
        return renderAccountTab();
    }
  };

  const renderAccountTab = () => (
    <div className="space-y-6">
      {/* Welcome Banner - Only show if profile incomplete */}
      {(!profile?.username || !profile?.date_of_birth) && (
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <span className="text-3xl">👋</span>
            <div>
              <h3 className="text-lg font-semibold text-ui-green mb-2">Welcome to Footy Coach!</h3>
              <p className="text-ui-green text-sm">
                Before you can join competitions and start making predictions, we just need to finish setting up your profile.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Why This Matters Section */}
      {(!profile?.username || !profile?.date_of_birth) && (
        <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-6">
          <h3 className="text-lg font-semibold text-brand-text mb-4">Why this matters</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <span className="text-ui-success mt-0.5">✓</span>
              <p className="text-sm text-brand-text-muted">Your profile lets you join and compete in Footy Coach competitions</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-ui-success mt-0.5">✓</span>
              <p className="text-sm text-brand-text-muted">Your age helps us unlock age-rated competitions and keep play fair</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-ui-success mt-0.5">✓</span>
              <p className="text-sm text-brand-text-muted">Your details help protect players and keep the game safe</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-ui-border">
            <p className="text-sm text-brand-text-muted italic">
              It only takes a minute — then you're ready to play ⚽
            </p>
          </div>
        </FcCard>
      )}

      <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-brand-text">Your Profile</h2>
          {!editing && (
            <FcButton variant="tertiary" type="submit"
              onClick={() => setEditing(true)}
              className=""
            >
              ✏️ Edit
            </FcButton>
          )}
        </div>

        {editing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-text mb-2">
                Username
                <span className="text-content-secondary text-xs ml-2">This is how other coaches will see you on leaderboards and in competitions</span>
              </label>
              <FcInput aria-label="Enter your username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="w-full border focus:border-transparent"
                placeholder="Enter your username"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-text mb-2">
                Date of Birth
                <span className="text-content-secondary text-xs ml-2">
                  ✓ Used to unlock age-appropriate competitions. You must be 13+, and some competitions require 18+.
                </span>
              </label>
              <FcInput aria-label="date of birth"
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                className="w-full border focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-text mb-2">
                Gender
                <span className="text-content-secondary text-xs ml-2">Optional – helps us improve inclusivity and future features</span>
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <FcInput aria-label="Male"
                    type="radio"
                    name="gender"
                    value="male"
                    checked={formData.gender === 'male'}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="w-4"
                  />
                  <span className="text-sm">Male</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <FcInput aria-label="Female"
                    type="radio"
                    name="gender"
                    value="female"
                    checked={formData.gender === 'female'}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="w-4"
                  />
                  <span className="text-sm">Female</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <FcInput aria-label="Other"
                    type="radio"
                    name="gender"
                    value="other"
                    checked={formData.gender === 'other'}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="w-4"
                  />
                  <span className="text-sm">Other</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <FcInput aria-label="Prefer not to say"
                    type="radio"
                    name="gender"
                    value=""
                    checked={!formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="w-4"
                  />
                  <span className="text-sm text-content-secondary">Prefer not to say</span>
                </label>
              </div>
            </div>
            
            {/* What happens next box */}
            {(!profile?.username || !profile?.date_of_birth) && (
              <div className="bg-surface-canvas border border-blue-200 rounded-lg p-4 mt-6">
                <h4 className="font-semibold text-ui-green mb-3">What happens next?</h4>
                <div className="space-y-2 text-sm text-ui-green">
                  <div className="flex items-start gap-2">
                    <span className="font-semibold">1 →</span>
                    <span>Your profile is saved</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold">2 →</span>
                    <span>You can join public or private competitions</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold">3 →</span>
                    <span>Your predictions start counting immediately</span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-4 pt-4">
              <FcButton variant="primary" type="submit"
                onClick={handleSaveProfile}
                disabled={saving || !formData.username || !formData.date_of_birth}
                className="flex-1 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : (!profile?.username || !profile?.date_of_birth) ? 'Save & Start Playing' : 'Save Changes'}
              </FcButton>
              {profile?.username && profile?.date_of_birth && (
                <FcButton variant="secondary" type="submit"
                  onClick={() => {
                    setEditing(false);
                    setFormData({
                      username: profile.username || '',
                      date_of_birth: profile.date_of_birth || '',
                      gender: profile.gender || '',
                      time_zone: profile.time_zone || ''
                    });
                  }}
                  className="flex-1 border"
                >
                  Cancel
                </FcButton>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-ui-border">
              <span className="text-sm font-medium text-brand-text">Username</span>
              <span className="text-brand-text-muted">{profile.username || '-'}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-ui-border">
              <span className="text-sm font-medium text-brand-text">Date of Birth</span>
              <span className="text-brand-text-muted">{profile.date_of_birth || '-'}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-ui-border">
              <span className="text-sm font-medium text-brand-text">Email</span>
              <span className="text-brand-text-muted">{session?.user?.email || '-'}</span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-sm font-medium text-brand-text">Member Since</span>
              <span className="text-brand-text-muted">{formatMemberSince()}</span>
            </div>
          </div>
        )}
      </FcCard>

      <SubscriptionCard />

      {/* Friend ID Section */}
      <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-6">
        <h2 className="text-xl font-semibold text-brand-text mb-6">Friend ID</h2>
        
        <div className="space-y-6">
          {/* Friend ID Display */}
          <div>
            <label className="block text-sm font-medium text-brand-text mb-3">Your Friend ID</label>
            <div className="bg-gradient-to-br from-[#284239] to-[#1f332a] rounded-lg p-4 text-white">
              <div className="text-center">
                <div className="text-2xl font-bold tracking-wider mb-2">
                  {referralData.referralCode || 'Loading...'}
                </div>
                <div className="text-sm text-white/70">Share this ID to connect with friends</div>
              </div>
            </div>
          </div>

          {/* QR Code */}
          <div>
            <label className="block text-sm font-medium text-brand-text mb-3">Friend QR Code</label>
            <div className="flex justify-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-ui-border">
              {referralData.referralCode ? (
                <QRCodeSVG 
                  value={`${window.location.origin}/friend/${referralData.referralCode}`}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              ) : (
                <LoadingState label="Loading QR Code..." />
              )}
            </div>
            <p className="text-xs text-content-secondary text-center mt-2">
              Friends can scan this code to add you instantly
            </p>
          </div>
        </div>
      </FcCard>

      {/* Account Statistics - Commented out
      <div className="bg-white rounded-lg shadow-sm border border-brand-border p-6">
        <h3 className="text-lg font-semibold text-brand-text mb-4">Account Statistics</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Total Competitions</div>
            <div className="text-2xl font-bold text-[#284239]">{stats.totalCompetitions}</div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Active Competitions</div>
            <div className="text-2xl font-bold text-[#284239]">{stats.activeCompetitions}</div>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Total Wins</div>
            <div className="text-2xl font-bold text-[#284239]">{stats.totalWins}</div>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Win Rate</div>
            <div className="text-2xl font-bold text-[#284239]">
              {stats.totalCompetitions > 0 
                ? `${Math.round((stats.totalWins / stats.totalCompetitions) * 100)}%`
                : '0%'
              }
            </div>
          </div>
        </div>
      </div>
      */}
    </div>
  );   

  const renderReferralsTab = () => (
    <div className="space-y-6">
      {/* Referral Code Card */}
      <div className="bg-gradient-to-br from-[#284239] to-[#1f332a] rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Your Referral Code</h3>
          <ShareButton
            url={`${window.location.origin}/invite/${referralData.referralCode}`}
            title="Join Footy Coach!"
            text="Join me on Footy Coach and compete in football prediction competitions!"
            type="referral"
            className="bg-action-primary hover:bg-[#a05728] text-white font-medium px-4 py-2 rounded-md inline-flex items-center gap-2"
          />
        </div>
        
        <FcCard as="div" padding="standard" className="bg-surface-card/10 rounded-lg p-4 mb-4">
          <div className="text-center">
            <div className="text-3xl font-bold tracking-wider mb-2">
              {referralData.referralCode || 'Loading...'}
            </div>
            <div className="text-sm text-white/70">Share this code with friends</div>
          </div>
        </FcCard>

        <div className="text-xs text-white/60 text-center break-all">
          {window.location.origin}/invite/{referralData.referralCode}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm text-content-secondary">Total Referrals</div>
              <div className="text-3xl font-bold text-ui-green">{referralData.totalReferrals}</div>
            </div>
            <div className="text-4xl">👥</div>
          </div>
        </FcCard>

        <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm text-content-secondary">Available Tickets</div>
              <div className="text-3xl font-bold text-ui-green">{referralData.availableTickets}</div>
            </div>
            <div className="text-4xl">🎫</div>
          </div>
        </FcCard>
      </div>

      {/* Badge Card */}
      <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-6">
        <h3 className="text-lg font-semibold text-brand-text mb-4">Referral Badges</h3>
        
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${badge.color} flex items-center justify-center text-4xl shadow-lg`}>
              {badge.icon}
            </div>
            <div>
              <div className="text-xl font-bold text-brand-text">{badge.name}</div>
              <div className="text-sm text-brand-text-muted">
                {referralData.totalReferrals} successful referral{referralData.totalReferrals !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {badge.next && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-content-secondary">
                Progress to {badge.next.name} Badge
              </span>
              <span className="text-sm font-semibold text-action-primary">
                {referralData.totalReferrals} / {badge.next.count}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-ui-copper to-[#a05728] h-3 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(progressToNext, 100)}%` }}
              />
            </div>
            <div className="mt-2 text-xs text-content-secondary text-center">
              {badge.next.count - referralData.totalReferrals} more referral{badge.next.count - referralData.totalReferrals !== 1 ? 's' : ''} to unlock {badge.next.name}!
            </div>
          </div>
        )}

        {!badge.next && (
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <div className="text-lg font-bold text-yellow-900 mb-1">🎉 Maximum Badge Achieved!</div>
            <div className="text-sm text-yellow-700">You're a referral champion!</div>
          </div>
        )}

        {/* Badge Milestones */}
        <div className="mt-6 grid grid-cols-3 gap-4">
          <div className={`text-center p-3 rounded-lg ${referralData.totalReferrals >= 5 ? 'bg-orange-50 border-2 border-orange-300' : 'bg-gray-50 opacity-50'}`}>
            <div className="text-2xl mb-1">🥉</div>
            <div className="text-xs font-semibold text-content-primary">Bronze</div>
            <div className="text-xs text-content-secondary">5 referrals</div>
          </div>
          <div className={`text-center p-3 rounded-lg ${referralData.totalReferrals >= 25 ? 'bg-gray-100 border-2 border-gray-400' : 'bg-gray-50 opacity-50'}`}>
            <div className="text-2xl mb-1">🥈</div>
            <div className="text-xs font-semibold text-content-primary">Silver</div>
            <div className="text-xs text-content-secondary">25 referrals</div>
          </div>
          <div className={`text-center p-3 rounded-lg ${referralData.totalReferrals >= 50 ? 'bg-yellow-50 border-2 border-yellow-400' : 'bg-gray-50 opacity-50'}`}>
            <div className="text-2xl mb-1">🥇</div>
            <div className="text-xs font-semibold text-content-primary">Gold</div>
            <div className="text-xs text-content-secondary">50 referrals</div>
          </div>
        </div>
      </FcCard>

      {/* Recent Referrals */}
      {referralData.recentReferrals.length > 0 && (
        <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-6">
          <h3 className="text-lg font-semibold text-brand-text mb-4">Recent Referrals</h3>
          <div className="space-y-2">
            {referralData.recentReferrals.map((referral, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-ui-green rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {referral.profiles?.username?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <div className="font-medium text-brand-text">
                      {referral.profiles?.username || 'User'}
                    </div>
                    <div className="text-xs text-content-secondary">
                      Joined {new Date(referral.completed_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>
                  </div>
                </div>
                <div className="text-ui-success font-semibold text-sm">
                  ✓ Active
                </div>
              </div>
            ))}
          </div>
        </FcCard>
      )}

      {/* How It Works */}
      <div className="bg-surface-canvas border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-ui-green mb-4 flex items-center">
          <span className="mr-2">💡</span> How Referrals Work
        </h3>
        <div className="space-y-3 text-sm text-ui-green">
          <div className="flex items-start">
            <span className="font-bold mr-2">1.</span>
            <span>Share your unique referral link with friends</span>
          </div>
          <div className="flex items-start">
            <span className="font-bold mr-2">2.</span>
            <span>When they sign up using your link, they become your referral</span>
          </div>
          <div className="flex items-start">
            <span className="font-bold mr-2">3.</span>
            <span>You earn 1 referral ticket for each successful referral</span>
          </div>
          <div className="flex items-start">
            <span className="font-bold mr-2">4.</span>
            <span>Use referral tickets to enter Scout competitions (up to 10 entries per matchday)</span>
          </div>
          <div className="flex items-start">
            <span className="font-bold mr-2">5.</span>
            <span>Earn badges: Bronze (5), Silver (25), Gold (50) referrals</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPreferencesTab = () => (
    <div className="space-y-6">
      <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-6">
        <h2 className="text-xl font-semibold text-brand-text mb-6">Account Preferences</h2>
        
        {editing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-text mb-2">Gender</label>
              <FcSelect aria-label="Gender"
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                className="w-full border focus:border-transparent"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </FcSelect>
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-text mb-2">Time Zone</label>
              <FcSelect aria-label="Time Zone"
                value={formData.time_zone}
                onChange={(e) => setFormData({ ...formData, time_zone: e.target.value })}
                className="w-full border focus:border-transparent"
              >
                <option value="">Select time zone</option>
                <option value="Europe/London">Europe/London (GMT)</option>
                <option value="Europe/Paris">Europe/Paris (CET)</option>
                <option value="America/New_York">America/New York (EST)</option>
                <option value="America/Los_Angeles">America/Los Angeles (PST)</option>
                <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                <option value="Australia/Sydney">Australia/Sydney (AEDT)</option>
              </FcSelect>
            </div>
            <div className="flex gap-4">
              <FcButton variant="tertiary" type="submit"
                onClick={handleSaveProfile}
                disabled={saving}
                className="flex-1"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </FcButton>
              <FcButton variant="secondary" type="submit"
                onClick={() => {
                  setEditing(false);
                  setFormData({
                    username: profile.username || '',
                    date_of_birth: profile.date_of_birth || '',
                    gender: profile.gender || '',
                    time_zone: profile.time_zone || ''
                  });
                }}
                className="flex-1 border"
              >
                Cancel
              </FcButton>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4 mb-6">
              <div className="flex justify-between items-center py-3 border-b border-ui-border">
                <span className="text-sm font-medium text-brand-text">Gender</span>
                <span className="text-brand-text-muted">{profile.gender || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-sm font-medium text-brand-text">Time Zone</span>
                <span className="text-brand-text-muted">{profile.time_zone || '-'}</span>
              </div>
            </div>
            <FcButton variant="tertiary" type="submit"
              onClick={() => setEditing(true)}
              className="w-full"
            >
              Edit Preferences
            </FcButton>
          </>
        )}
      </FcCard>

      <div className="bg-surface-canvas border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-ui-green mb-2 flex items-center">
          <span className="mr-2">ℹ️</span> About Preferences
        </h3>
        <div className="space-y-2 text-sm text-ui-green">
          <p><strong>Gender:</strong> This information helps us personalize your experience.</p>
          <p><strong>Time Zone:</strong> Used to schedule notifications and display times in your local timezone.</p>
        </div>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-6">
        <h2 className="text-xl font-semibold text-brand-text mb-6">Notification Settings</h2>
        
        <div className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <div className="flex items-center mb-1">
                <span className="text-lg mr-2">🔔</span>
                <h3 className="font-semibold text-brand-text">Push Notifications</h3>
              </div>
              <p className="text-sm text-brand-text-muted">
                Receive push notifications for match reminders and important updates
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer ml-4">
              <FcInput aria-label="Notification Settings"
                type="checkbox"
                checked={profile.notifications_enabled}
                onChange={handleToggleNotifications}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#284239]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-surface-card after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-ui-green"></div>
            </label>
          </div>
        </div>
      </FcCard>

      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-ui-success mb-4 flex items-center">
          <span className="mr-2">💡</span> Notification Types
        </h3>
        <div className="space-y-3 text-sm text-ui-success">
          <div className="flex items-start">
            <span className="font-bold mr-2">•</span>
            <span><strong>Team Selection Reminders:</strong> Get notified before the deadline to make your team selection</span>
          </div>
          <div className="flex items-start">
            <span className="font-bold mr-2">•</span>
            <span><strong>Match Results:</strong> Receive updates when matches are completed</span>
          </div>
          <div className="flex items-start">
            <span className="font-bold mr-2">•</span>
            <span><strong>Competition Updates:</strong> Stay informed about competition status changes</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        
        <div role="main" id="fc-main-content" className="md:ml-64 pb-20 md:pb-8 fc-workspace">
          <div className="p-4 md:p-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-center min-h-[50vh]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#284239] mx-auto mb-4"></div>
                  <LoadingState label="Loading your profile..." />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        
        <div role="main" id="fc-main-content" className="md:ml-64 pb-20 md:pb-8 fc-workspace">
          <div className="p-4 md:p-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-center min-h-[50vh]">
                <div className="text-center">
                  <div className="text-ui-green text-6xl mb-4">👤</div>
                  <p className="text-content-secondary font-medium mb-6">No profile found. Let's get you set up!</p>
                  <FcButton variant="tertiary" type="submit"
                    onClick={() => navigate('/setup-profile')}
                    className=""
                  >
                    Create Profile
                  </FcButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg">
      <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
      <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
      
      <div role="main" id="fc-main-content" className="md:ml-64 pb-20 md:pb-8 fc-workspace">
        <div className="p-4 md:p-8">
          <div className="max-w-4xl mx-auto">
            {/* Profile Header */}
            <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-6 mb-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                {/* Left: Avatar & Info */}
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#284239] to-[#1f332a] flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                    {getInitials()}
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-brand-text">{profile.username}</h1>
                    <p className="text-brand-text-muted text-sm">@{profile.username?.toLowerCase().replace(/\s+/g, '')}</p>
                    <p className="text-brand-text-muted text-xs mt-1">Member since {formatMemberSince()}</p>
                  </div>
                </div>
                
                {/* Right: Quick Stats */}
                <div className="flex gap-4">
                  <div className="text-center px-4 py-2 bg-surface-canvas rounded-lg">
                    <div className="text-2xl font-bold text-ui-green">{stats.totalCompetitions}</div>
                    <div className="text-xs text-content-secondary">Competitions</div>
                  </div>
                  <div className="text-center px-4 py-2 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-ui-green">{stats.totalWins}</div>
                    <div className="text-xs text-content-secondary">Wins</div>
                  </div>
                  <div className="text-center px-4 py-2 bg-surface-canvas rounded-lg">
                    <div className="text-2xl font-bold text-ui-green">{referralData.totalReferrals}</div>
                    <div className="text-xs text-content-secondary">Referrals</div>
                  </div>
                </div>
              </div>
            </FcCard>

            {/* Tab Navigation */}
            <FcTabs label="Account sections" value={activeTab} onValueChange={setActiveTab} items={[{id:"account",label:<>
                  👤 Account
                </>},{id:"referrals",label:<>
                  🎁 Referrals
                </>},{id:"preferences",label:<>
                  ⚙️ Preferences
                </>},{id:"notifications",label:<>
                  🔔 Notifications
                </>}]}>

            {/* Tab Content */}
            {renderTabContent()}
</FcTabs>
          </div>
        </div>
      </div>
    </div>
  );
}
